import { createContext, useContext, useState, useEffect, useCallback } from 'react'

/* ===================== Traductions ===================== */
const TR = {
  fr: {
    'nav.dashboard': 'Tableau de bord',
    'nav.search': 'Recherche',
    'nav.crm': 'CRM Pipeline',
    'nav.leads': '👤 Contacts LinkedIn',
    'nav.agenda': 'Agenda',
    'nav.tour': '🗺️ Tournée',
    'nav.notes': 'Mes notes',
    'nav.report': 'Compte rendu',
    'nav.bydemes': 'Clients Bydemes',
    'nav.export': 'Export',
    'nav.linkedin': 'Posts LinkedIn',
    'nav.suppliers': 'Fournisseurs',
    'nav.watchlist': 'Veille LinkedIn',
    'nav.users': 'Utilisateurs',
    'nav.deploy': 'Déploiement',
    'nav.settings': 'Paramètres',
    'nav.tagline': 'Prospection B2B',

    'header.online': 'En ligne',
    'header.logout': 'Se déconnecter',

    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.delete': 'Supprimer',
    'common.edit': 'Modifier',
    'common.add': 'Ajouter',
    'common.search': 'Rechercher',
    'common.loading': 'Chargement…',
    'common.close': 'Fermer',
    'common.all': 'Tous',
    'common.theme': 'Thème',
    'common.lang': 'Langue',

    'login.title': 'ProspectFlow',
    'login.subtitle': 'Prospection B2B intelligente',
    'login.email': 'Email',
    'login.password': 'Mot de passe',
    'login.signin': 'Se connecter',
    'login.signup': 'Créer un compte',
    'login.google': 'Continuer avec Google',
    'login.firstname': 'Prénom',
    'login.lastname': 'Nom',

    'status.nouveau': 'Nouveau',
    'status.a_qualifier': 'À qualifier',
    'status.contacte': 'Contacté',
    'status.relance': 'Relancé',
    'status.appel': 'Appel',
    'status.rdv_planifie': 'RDV planifié',
    'status.tournee': 'Tournée',
    'status.client_gagne': 'Gagné',
    'status.perdu': 'Perdu',

    'crm.searchPlaceholder': 'Société, dirigeant, ville, NAF, SIREN, email, tél...',
    'crm.allNaf': 'Tous NAF',
    'crm.allDepts': 'Tous depts',
  },
  en: {
    'nav.dashboard': 'Dashboard',
    'nav.search': 'Search',
    'nav.crm': 'CRM Pipeline',
    'nav.leads': '👤 LinkedIn Contacts',
    'nav.agenda': 'Calendar',
    'nav.tour': '🗺️ Tour',
    'nav.notes': 'My notes',
    'nav.report': 'Monthly report',
    'nav.bydemes': 'Bydemes Clients',
    'nav.export': 'Export',
    'nav.linkedin': 'LinkedIn Posts',
    'nav.suppliers': 'Suppliers',
    'nav.watchlist': 'LinkedIn Watch',
    'nav.users': 'Users',
    'nav.deploy': 'Deploy',
    'nav.settings': 'Settings',
    'nav.tagline': 'B2B Prospecting',

    'header.online': 'Online',
    'header.logout': 'Log out',

    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.search': 'Search',
    'common.loading': 'Loading…',
    'common.close': 'Close',
    'common.all': 'All',
    'common.theme': 'Theme',
    'common.lang': 'Language',

    'login.title': 'ProspectFlow',
    'login.subtitle': 'Smart B2B Prospecting',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.signin': 'Sign in',
    'login.signup': 'Create account',
    'login.google': 'Continue with Google',
    'login.firstname': 'First name',
    'login.lastname': 'Last name',

    'status.nouveau': 'New',
    'status.a_qualifier': 'To qualify',
    'status.contacte': 'Contacted',
    'status.relance': 'Followed up',
    'status.appel': 'Call',
    'status.rdv_planifie': 'Meeting set',
    'status.tournee': 'Field tour',
    'status.client_gagne': 'Won',
    'status.perdu': 'Lost',

    'crm.searchPlaceholder': 'Company, director, city, NAF, SIREN, email, phone...',
    'crm.allNaf': 'All NAF',
    'crm.allDepts': 'All depts',
  },
}

/* ===================== Context ===================== */
const I18nCtx = createContext(null)

function applyTheme(theme) {
  const el = document.documentElement
  el.classList.remove('theme-dark', 'theme-light')
  el.classList.add(theme === 'light' ? 'theme-light' : 'theme-dark')
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem('lang') || 'fr')
  const [theme, setThemeState] = useState(() => localStorage.getItem('theme') || 'dark')

  useEffect(() => { applyTheme(theme) }, [theme])

  const setLang = useCallback((l) => {
    localStorage.setItem('lang', l)
    setLangState(l)
  }, [])

  const setTheme = useCallback((t) => {
    localStorage.setItem('theme', t)
    setThemeState(t)
  }, [])

  const t = useCallback((key) => {
    return (TR[lang] && TR[lang][key]) || (TR.fr[key]) || key
  }, [lang])

  return (
    <I18nCtx.Provider value={{ lang, setLang, theme, setTheme, t }}>
      {children}
    </I18nCtx.Provider>
  )
}

export function useI18n() {
  const ctx = useContext(I18nCtx)
  if (!ctx) return { lang: 'fr', setLang: () => {}, theme: 'dark', setTheme: () => {}, t: (k) => k }
  return ctx
}

// Applique le thème immédiatement au chargement (avant React)
applyTheme(localStorage.getItem('theme') || 'dark')
